import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Trash2,
  Copy,
  Layers,
  Lock,
  Unlock,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  MoreHorizontal,
} from "lucide-react";

/**
 * FloatingToolbar - Contextual toolbar that appears near selected element
 * Provides quick actions: delete, duplicate, layer order, lock, alignment
 */
export function FloatingToolbar({
  element,
  position,
  onDelete,
  onDuplicate,
  onUpdate,
  onBringForward,
  onSendBackward,
  onBringToFront,
  onSendToBack,
  canvasScale = 1,
}) {
  if (!element || !position) return null;

  const isText = element.type === "text" || element.type === "pedagogical_text";
  const isLocked = element.locked;

  const handleToggleLock = () => {
    onUpdate(element.id, { locked: !isLocked });
  };

  const handleToggleVisibility = () => {
    onUpdate(element.id, { visible: element.visible === false ? true : false });
  };

  const handleTextAlign = (align) => {
    onUpdate(element.id, { align });
  };

  // Calculate toolbar position (above element, centered)
  const toolbarStyle = {
    position: "absolute",
    left: `${position.x + position.width / 2}px`,
    top: `${position.y - 50}px`,
    transform: "translateX(-50%)",
    zIndex: 100,
  };

  return (
    <div
      style={toolbarStyle}
      className="bg-white rounded-lg shadow-lg border border-slate-200 px-1 py-1 flex items-center gap-0.5 animate-in fade-in zoom-in-95 duration-150"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Quick actions */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => onDuplicate(element)}
        title="Dupliquer"
      >
        <Copy className="w-4 h-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={handleToggleLock}
        title={isLocked ? "Deverrouiller" : "Verrouiller"}
      >
        {isLocked ? (
          <Lock className="w-4 h-4 text-orange-500" />
        ) : (
          <Unlock className="w-4 h-4" />
        )}
      </Button>

      {/* Text alignment (only for text elements) */}
      {isText && (
        <>
          <div className="w-px h-5 bg-slate-200 mx-1" />
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 w-8 p-0 ${element.align === "left" ? "bg-slate-100" : ""}`}
            onClick={() => handleTextAlign("left")}
            title="Aligner a gauche"
          >
            <AlignLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 w-8 p-0 ${element.align === "center" ? "bg-slate-100" : ""}`}
            onClick={() => handleTextAlign("center")}
            title="Centrer"
          >
            <AlignCenter className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 w-8 p-0 ${element.align === "right" ? "bg-slate-100" : ""}`}
            onClick={() => handleTextAlign("right")}
            title="Aligner a droite"
          >
            <AlignRight className="w-4 h-4" />
          </Button>
        </>
      )}

      <div className="w-px h-5 bg-slate-200 mx-1" />

      {/* Layer controls dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Calques">
            <Layers className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          <DropdownMenuItem onClick={onBringToFront}>
            <ChevronUp className="w-4 h-4 mr-2" />
            Premier plan
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onBringForward}>
            <ChevronUp className="w-4 h-4 mr-2" />
            Avancer
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onSendBackward}>
            <ChevronDown className="w-4 h-4 mr-2" />
            Reculer
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onSendToBack}>
            <ChevronDown className="w-4 h-4 mr-2" />
            Arriere-plan
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* More actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleToggleVisibility}>
            {element.visible === false ? (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Afficher
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Masquer
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onDelete(element.id)}
            className="text-red-600 focus:text-red-600"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete button */}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
        onClick={() => onDelete(element.id)}
        title="Supprimer"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

/**
 * SelectionHandles - Resize handles around selected element
 */
export function SelectionHandles({
  element,
  position,
  onResize,
  onResizeEnd,
  canvasScale = 1,
}) {
  const [dragging, setDragging] = useState(null);
  const startRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  if (!element || !position) return null;

  const handleSize = 10;
  const handles = [
    { id: "nw", cursor: "nwse-resize", x: -handleSize / 2, y: -handleSize / 2 },
    { id: "n", cursor: "ns-resize", x: position.width / 2 - handleSize / 2, y: -handleSize / 2 },
    { id: "ne", cursor: "nesw-resize", x: position.width - handleSize / 2, y: -handleSize / 2 },
    { id: "e", cursor: "ew-resize", x: position.width - handleSize / 2, y: position.height / 2 - handleSize / 2 },
    { id: "se", cursor: "nwse-resize", x: position.width - handleSize / 2, y: position.height - handleSize / 2 },
    { id: "s", cursor: "ns-resize", x: position.width / 2 - handleSize / 2, y: position.height - handleSize / 2 },
    { id: "sw", cursor: "nesw-resize", x: -handleSize / 2, y: position.height - handleSize / 2 },
    { id: "w", cursor: "ew-resize", x: -handleSize / 2, y: position.height / 2 - handleSize / 2 },
  ];

  // Only show corner handles for circles
  const visibleHandles = element.type === "circle" 
    ? handles.filter(h => ["nw", "ne", "se", "sw"].includes(h.id))
    : handles;

  return (
    <div
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${position.width}px`,
        height: `${position.height}px`,
        pointerEvents: "none",
      }}
    >
      {/* Selection border */}
      <div
        className="absolute inset-0 border-2 border-indigo-500 rounded"
        style={{ pointerEvents: "none" }}
      />

      {/* Resize handles */}
      {visibleHandles.map((handle) => (
        <div
          key={handle.id}
          className="absolute bg-white border-2 border-indigo-500 rounded-sm hover:bg-indigo-100 transition-colors"
          style={{
            left: `${handle.x}px`,
            top: `${handle.y}px`,
            width: `${handleSize}px`,
            height: `${handleSize}px`,
            cursor: handle.cursor,
            pointerEvents: "auto",
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            // Handle resize logic would go here
            // This is a simplified version - full implementation would track mouse movement
          }}
        />
      ))}

      {/* Element type badge */}
      <div
        className="absolute -top-6 left-0 bg-indigo-500 text-white text-xs px-2 py-0.5 rounded"
        style={{ pointerEvents: "none" }}
      >
        {element.type === "pedagogical_text" ? "Texte pedagogique" : element.type}
      </div>
    </div>
  );
}
